import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IndicatorData } from '../../../models/indicator-data';
import { DecimalPipe, KeyValuePipe, NgClass, NgIf } from '@angular/common';
import { SafeNumberPipe } from '../../../pipes/safe-number.pipe';
import { KeyValueDistricts } from '../../../models/geo-data';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { IndicatorChildrenDropdownComponent } from '../../atoms/indicator-children-dropdown/indicator-children-dropdown.component';
import { filterFeaturesBySearchWord } from '../../../stores/geo/geo-utils';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { ScrollPanelModule } from 'primeng/scrollpanel';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    KeyValuePipe,
    SafeNumberPipe,
    SearchInputComponent,
    DividerModule,
    ButtonModule,
    IndicatorChildrenDropdownComponent,
    NgIf,
    FormsModule,
    InputTextModule,
    ScrollPanelModule,
    NgClass,
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
  providers: [DecimalPipe, SafeNumberPipe],
})
export class DataTableComponent implements OnInit, OnChanges {
  readonly comparisonStore = inject(ComparisonValueStore);
  @Input() data?: IndicatorData;
  @Input() districts!: KeyValueDistricts;
  @Input() global = false;
  @Input() isInvalid = false;
  @Input() errorKeys: string[] = [];
  @Input() errorValues: { [key: string]: any } = {};
  @Input() warningKeys: string[] = [];
  filteredDistricts!: KeyValueDistricts;

  /**
   * Filters the features based on the provided search word and updates the filteredDistricts property.
   *
   * @param {string} searchWord - The term used to filter features from the districts list.
   * @return {void} This method does not return a value.
   */
  filterFeatures(searchWord: string): void {
    this.filteredDistricts = filterFeaturesBySearchWord(
      this.districts,
      searchWord
    );
  }

  /**
   * Angular lifecycle hook that is called after the component's view has been fully initialized.
   * This method initializes the component by applying an initial filter to the features.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.filterFeatures('');
  }

  /**
   * Determines whether the specified key is associated with any errors.
   *
   * @param {string} key - The key to check for associated errors.
   * @return {boolean} Returns true if there are errors associated with the key; otherwise, false.
   */
  hasErrors(key: string): boolean {
    return this.errorKeys.length > 0 && this.errorKeys.includes(key);
  }

  /**
   * Retrieves the error value associated with the given key.
   *
   * @param {string} key - The key used to look up the error value.
   * @return {any} The error value corresponding to the specified key.
   */
  getErrorValue(key: string): any {
    return this.errorValues[key];
  }

  /**
   * Determines whether the specified key is associated with any errors.
   *
   * @param {string} key - The key to check for associated errors.
   * @return {boolean} Returns true if there are errors associated with the key; otherwise, false.
   */
  hasWarnings(key: string): boolean {
    return this.warningKeys.length > 0 && this.warningKeys.includes(key);
  }

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * This method is used to handle updates when input properties change.
   *
   * @param {SimpleChanges} changes - The object containing the changed input properties and their previous/current values.
   * @return {void} This method does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['districts']) this.filterFeatures('');
  }
}
